import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import {
  PromoCampaign,
  CampaignEnrollment,
  CampaignPerformance,
  ProjectedVsActual,
  CustomFieldDef,
  getCampaign,
  updateCampaign,
  enrollCustomer,
  getEnrollments,
  deleteEnrollment,
  getCampaignPerformance,
  getProjectedVsActual,
} from '../../services/promoCampaignAPI';
import api from '../../services/api';
import './CRM.css';

const STATUS_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  DRAFT: { bg: '#f3f4f6', color: '#6b7280' },
  ACTIVE: { bg: '#d1fae5', color: '#065f46' },
  PAUSED: { bg: '#fef3c7', color: '#92400e' },
  COMPLETED: { bg: '#dbeafe', color: '#1e40af' },
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const formatCurrency = (value: string | null | undefined): string => {
  if (!value) return '$0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return currencyFormatter.format(num);
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
};

const formatPct = (value: string | null | undefined): string => {
  if (!value) return '0%';
  const num = parseFloat(value);
  if (isNaN(num)) return '0%';
  return `${num.toFixed(1)}%`;
};

interface CustomerSearchResult {
  id: number;
  name: string;
  customer_type?: string;
}

const PromoCampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const campaignId = Number(id);

  // Campaign state
  const [campaign, setCampaign] = useState<PromoCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<PromoCampaign>>({});
  const [saving, setSaving] = useState(false);

  // Performance
  const [performance, setPerformance] = useState<CampaignPerformance | null>(null);

  // Enrollments
  const [enrollments, setEnrollments] = useState<CampaignEnrollment[]>([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

  // Enroll modal
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState<CustomerSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSearchResult | null>(null);
  const [enrollCustomFields, setEnrollCustomFields] = useState<Record<string, any>>({});
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');

  // Projected vs Actual
  const [projectedVsActual, setProjectedVsActual] = useState<ProjectedVsActual | null>(null);
  const [projectedLoading, setProjectedLoading] = useState(false);
  const [expandedEnrollmentIdx, setExpandedEnrollmentIdx] = useState<number | null>(null);

  // Load all data
  const loadCampaign = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getCampaign(campaignId);
      setCampaign(data);
    } catch (err) {
      console.error('Failed to load campaign:', err);
      setError('Failed to load campaign.');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const loadPerformance = useCallback(async () => {
    try {
      const data = await getCampaignPerformance(campaignId);
      setPerformance(data);
    } catch (err) {
      console.error('Failed to load performance:', err);
    }
  }, [campaignId]);

  const loadEnrollments = useCallback(async () => {
    setEnrollmentsLoading(true);
    try {
      const data = await getEnrollments(campaignId);
      setEnrollments(data);
    } catch (err) {
      console.error('Failed to load enrollments:', err);
    } finally {
      setEnrollmentsLoading(false);
    }
  }, [campaignId]);

  const loadProjectedVsActual = useCallback(async () => {
    setProjectedLoading(true);
    try {
      const data = await getProjectedVsActual(campaignId);
      setProjectedVsActual(data);
    } catch (err) {
      console.error('Failed to load projected vs actual:', err);
    } finally {
      setProjectedLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    if (!campaignId || isNaN(campaignId)) {
      setError('Invalid campaign ID.');
      setLoading(false);
      return;
    }
    loadCampaign();
    loadPerformance();
    loadEnrollments();
    loadProjectedVsActual();
  }, [campaignId, loadCampaign, loadPerformance, loadEnrollments, loadProjectedVsActual]);

  // ---- Edit handlers ----
  const startEditing = () => {
    if (!campaign) return;
    setEditData({
      name: campaign.name,
      campaign_type: campaign.campaign_type,
      status: campaign.status,
      start_date: campaign.start_date,
      end_date: campaign.end_date,
      budget: campaign.budget,
      description: campaign.description,
      target_animal_type: campaign.target_animal_type,
      target_formula: campaign.target_formula,
      notes: campaign.notes,
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditData({});
  };

  const handleEditChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!campaign) return;
    setSaving(true);
    setError('');
    try {
      const updated = await updateCampaign(campaign.id, editData);
      setCampaign(updated);
      setEditing(false);
      setEditData({});
      loadPerformance();
    } catch (err) {
      console.error('Failed to update campaign:', err);
      setError('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  // ---- Customer search (debounced) ----
  useEffect(() => {
    if (!showEnrollModal) return;
    if (customerSearch.trim().length < 2) {
      setCustomerResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.get('/promo-campaigns/search-customers/', { params: { q: customerSearch.trim() } });
        const results = Array.isArray(res.data) ? res.data : res.data.results || [];
        setCustomerResults(results.slice(0, 10));
      } catch (err) {
        console.error('Customer search failed:', err);
        setCustomerResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [customerSearch, showEnrollModal]);

  // ---- Enroll handlers ----
  const openEnrollModal = () => {
    setShowEnrollModal(true);
    setCustomerSearch('');
    setCustomerResults([]);
    setSelectedCustomer(null);
    setEnrollCustomFields({});
    setEnrollError('');
  };

  const handleEnrollFieldChange = (key: string, value: any) => {
    setEnrollCustomFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleEnrollSubmit = async () => {
    if (!selectedCustomer || !campaign) return;

    // Validate required fields
    const defs = campaign.custom_field_definitions || [];
    for (const def of defs) {
      if (def.required) {
        const val = enrollCustomFields[def.key];
        if (val === undefined || val === null || val === '') {
          setEnrollError(`"${def.label}" is required.`);
          return;
        }
      }
    }

    setEnrolling(true);
    setEnrollError('');
    try {
      await enrollCustomer(campaignId, {
        customer_id: selectedCustomer.id,
        custom_fields: enrollCustomFields,
      });
      setShowEnrollModal(false);
      loadEnrollments();
      loadPerformance();
      loadProjectedVsActual();
    } catch (err: any) {
      console.error('Enrollment failed:', err);
      const msg = err?.response?.data?.detail || err?.response?.data?.error || 'Failed to enroll customer.';
      setEnrollError(msg);
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemoveEnrollment = async (enrollmentId: number) => {
    if (!window.confirm('Remove this enrollment?')) return;
    try {
      await deleteEnrollment(campaignId, enrollmentId);
      loadEnrollments();
      loadPerformance();
      loadProjectedVsActual();
    } catch (err) {
      console.error('Failed to remove enrollment:', err);
      setError('Failed to remove enrollment.');
    }
  };

  // ---- Render helpers ----
  const renderStatusBadge = (status: string, label?: string) => {
    const badge = STATUS_BADGE_COLORS[status] || STATUS_BADGE_COLORS.DRAFT;
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: badge.bg,
          color: badge.color,
          textTransform: 'uppercase',
        }}
      >
        {label || status}
      </span>
    );
  };

  const renderCustomFieldInput = (def: CustomFieldDef) => {
    const value = enrollCustomFields[def.key] ?? '';
    switch (def.type) {
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleEnrollFieldChange(def.key, e.target.value ? Number(e.target.value) : '')}
            step="any"
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleEnrollFieldChange(def.key, e.target.value)}
          />
        );
      case 'boolean':
        return (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleEnrollFieldChange(def.key, e.target.checked)}
              style={{ width: '18px', height: '18px' }}
            />
            Yes
          </label>
        );
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleEnrollFieldChange(def.key, e.target.value)}
            placeholder={def.label}
          />
        );
    }
  };

  // ---- Loading / Error ----
  if (loading) {
    return (
      <div className="crm-page">
        <div className="loading">Loading campaign...</div>
      </div>
    );
  }

  if (error && !campaign) {
    return (
      <div className="crm-page">
        <Link to="/crm/promo-campaigns" className="back-link">
          &larr; Back to Promo Campaigns
        </Link>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!campaign) return null;

  const customFieldDefs: CustomFieldDef[] = campaign.custom_field_definitions || [];

  return (
    <div className="crm-page">
      {/* Back link */}
      <Link to="/crm/promo-campaigns" className="back-link">
        &larr; Back to Promo Campaigns
      </Link>

      {error && <div className="error-message" style={{ marginTop: '0.5rem' }}>{error}</div>}

      {/* ==================== Campaign Info Card ==================== */}
      <div className="info-card" style={{ marginTop: '0.75rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#1a1a1a' }}>
              {editing ? (
                <input
                  type="text"
                  name="name"
                  value={editData.name || ''}
                  onChange={handleEditChange}
                  style={{ fontSize: '1.5rem', fontWeight: 700, border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.25rem 0.5rem', width: '100%' }}
                />
              ) : (
                campaign.name
              )}
            </h1>
            {!editing && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {renderStatusBadge(campaign.status, campaign.status_display)}
                <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                  {campaign.campaign_type_display}
                </span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {editing ? (
              <>
                <button className="btn btn-secondary" onClick={cancelEditing} disabled={saving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <button className="btn btn-secondary" onClick={startEditing}>
                Edit
              </button>
            )}
          </div>
        </div>

        {editing ? (
          <div className="info-grid" style={{ gap: '1rem' }}>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={editData.status || ''} onChange={handleEditChange}>
                <option value="DRAFT">Draft</option>
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">Paused</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>
            <div className="form-group">
              <label>Campaign Type</label>
              <select name="campaign_type" value={editData.campaign_type || ''} onChange={handleEditChange}>
                <option value="FREE_FEED">Free Feed</option>
                <option value="DISCOUNT">Discount</option>
                <option value="TRIAL">Trial</option>
              </select>
            </div>
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" name="start_date" value={editData.start_date || ''} onChange={handleEditChange} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input type="date" name="end_date" value={editData.end_date || ''} onChange={handleEditChange} />
            </div>
            <div className="form-group">
              <label>Budget</label>
              <input type="number" name="budget" value={editData.budget || ''} onChange={handleEditChange} step="0.01" min="0" />
            </div>
            <div className="form-group">
              <label>Target Animal Type</label>
              <select name="target_animal_type" value={editData.target_animal_type || ''} onChange={handleEditChange}>
                <option value="">-- None --</option>
                <option value="POULTRY">Poultry</option>
                <option value="SWINE">Swine</option>
                <option value="HORSE">Horse</option>
                <option value="RUMINANTS">Ruminants</option>
                <option value="SPECIAL">Special</option>
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Description</label>
              <textarea name="description" value={editData.description || ''} onChange={handleEditChange} rows={3} />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <textarea name="notes" value={editData.notes || ''} onChange={handleEditChange} rows={2} />
            </div>
          </div>
        ) : (
          <div className="info-grid">
            <div className="info-item">
              <label>Start Date</label>
              <span>{formatDate(campaign.start_date)}</span>
            </div>
            <div className="info-item">
              <label>End Date</label>
              <span>{formatDate(campaign.end_date)}</span>
            </div>
            <div className="info-item">
              <label>Budget</label>
              <span>{formatCurrency(campaign.budget)}</span>
            </div>
            <div className="info-item">
              <label>Target Animal Type</label>
              <span>{campaign.target_animal_type || '-'}</span>
            </div>
            <div className="info-item">
              <label>Target Formula</label>
              <span>{campaign.target_formula_name || '-'}</span>
            </div>
            <div className="info-item">
              <label>Created</label>
              <span>{formatDate(campaign.created_at)}</span>
            </div>
            {campaign.description && (
              <div className="info-item full-width">
                <label>Description</label>
                <span>{campaign.description}</span>
              </div>
            )}
            {campaign.notes && (
              <div className="info-item full-width">
                <label>Notes</label>
                <span>{campaign.notes}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== Performance Summary Cards ==================== */}
      {performance && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}
        >
          <div
            style={{
              background: '#f0fdf4',
              borderRadius: '8px',
              padding: '1.25rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ fontSize: '0.8rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 500, marginBottom: '0.5rem' }}>
              Total Invested
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#065f46' }}>
              {formatCurrency(performance.total_invested)}
            </div>
          </div>

          <div
            style={{
              background: '#eff6ff',
              borderRadius: '8px',
              padding: '1.25rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ fontSize: '0.8rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 500, marginBottom: '0.5rem' }}>
              Total Revenue
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e40af' }}>
              {formatCurrency(performance.total_revenue)}
            </div>
          </div>

          <div
            style={{
              background: '#fefce8',
              borderRadius: '8px',
              padding: '1.25rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ fontSize: '0.8rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 500, marginBottom: '0.5rem' }}>
              ROI %
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#92400e' }}>
              {formatPct(performance.roi_pct)}
            </div>
          </div>

          <div
            style={{
              background: '#faf5ff',
              borderRadius: '8px',
              padding: '1.25rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ fontSize: '0.8rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 500, marginBottom: '0.5rem' }}>
              Customers Enrolled
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#7c3aed' }}>
              {performance.enrolled_count}
            </div>
          </div>
        </div>
      )}

      {/* ==================== Enrollments Table ==================== */}
      <div className="info-card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Enrollments</h3>
          <button className="btn btn-primary btn-sm" onClick={openEnrollModal}>
            + Enroll Customer
          </button>
        </div>

        {enrollmentsLoading ? (
          <div className="loading">Loading enrollments...</div>
        ) : enrollments.length === 0 ? (
          <div className="empty-state small">
            <p>No customers enrolled yet.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="leads-table">
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Enrolled Date</th>
                  <th>Status</th>
                  {customFieldDefs.map((def) => (
                    <th key={def.key}>{def.label}</th>
                  ))}
                  <th>Promo Lbs</th>
                  <th>Promo Value</th>
                  <th>Orders</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.map((enrollment) => (
                  <tr key={enrollment.id} style={{ cursor: 'default' }}>
                    <td>
                      <strong>{enrollment.customer_name}</strong>
                    </td>
                    <td style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                      {formatDate(enrollment.enrolled_at)}
                    </td>
                    <td style={{ fontSize: '0.85rem', color: '#4b5563' }}>
                      {enrollment.status}
                    </td>
                    {customFieldDefs.map((def) => (
                      <td key={def.key} style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                        {enrollment.custom_fields?.[def.key] !== undefined
                          ? def.type === 'boolean'
                            ? enrollment.custom_fields[def.key] ? 'Yes' : 'No'
                            : def.type === 'date'
                              ? formatDate(enrollment.custom_fields[def.key])
                              : String(enrollment.custom_fields[def.key])
                          : '-'}
                      </td>
                    ))}
                    <td style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                      {enrollment.total_promo_lbs ? `${parseFloat(enrollment.total_promo_lbs).toLocaleString()} lbs` : '-'}
                    </td>
                    <td style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                      {formatCurrency(enrollment.total_promo_value)}
                    </td>
                    <td style={{ fontSize: '0.9rem' }}>
                      {enrollment.orders_count}
                    </td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveEnrollment(enrollment.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ==================== Projected vs Actual Section ==================== */}
      {projectedLoading ? (
        <div className="loading">Loading projected vs actual data...</div>
      ) : projectedVsActual && (
        <div className="info-card" style={{ marginBottom: '1.5rem' }}>
          <h3>Projected vs Actual</h3>

          {/* Summary cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}
          >
            <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 500, marginBottom: '0.25rem' }}>
                Projected Total
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#065f46' }}>
                {parseFloat(projectedVsActual.summary.projected_total || '0').toLocaleString()} lbs
              </div>
            </div>
            <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 500, marginBottom: '0.25rem' }}>
                Actual Total
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e40af' }}>
                {parseFloat(projectedVsActual.summary.actual_total || '0').toLocaleString()} lbs
              </div>
            </div>
            <div style={{ background: '#fefce8', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 500, marginBottom: '0.25rem' }}>
                Capture Rate
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#92400e' }}>
                {formatPct(projectedVsActual.summary.avg_capture_pct)}
              </div>
            </div>
            <div style={{ background: '#faf5ff', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 500, marginBottom: '0.25rem' }}>
                Total Promo Cost
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#7c3aed' }}>
                {formatCurrency(performance?.total_invested)}
              </div>
            </div>
            <div style={{ background: '#fff7ed', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 500, marginBottom: '0.25rem' }}>
                ROI %
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#c2410c' }}>
                {formatPct(projectedVsActual.summary.roi_pct)}
              </div>
            </div>
          </div>

          {/* Monthly Bar Chart: Projected vs Actual */}
          {projectedVsActual.monthly.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', color: '#374151' }}>
                Monthly Projected vs Actual (lbs)
              </h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={projectedVsActual.monthly.map(m => ({
                  month: m.month,
                  projected: parseFloat(m.projected_lbs || '0'),
                  actual: parseFloat(m.actual_lbs || '0'),
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value: number) => `${value.toLocaleString()} lbs`} />
                  <Legend />
                  <Bar dataKey="projected" name="Projected" fill="#93c5fd" />
                  <Bar dataKey="actual" name="Actual" fill="#065f46" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Cumulative ROI Line Chart */}
          {projectedVsActual.monthly.length > 1 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', color: '#374151' }}>
                Cumulative Feed Purchased (lbs)
              </h4>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={(() => {
                  let cumProj = 0;
                  let cumActual = 0;
                  return projectedVsActual.monthly.map(m => {
                    cumProj += parseFloat(m.projected_lbs || '0');
                    cumActual += parseFloat(m.actual_lbs || '0');
                    return { month: m.month, projected: Math.round(cumProj), actual: Math.round(cumActual) };
                  });
                })()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value: number) => `${value.toLocaleString()} lbs`} />
                  <Legend />
                  <Line type="monotone" dataKey="projected" name="Projected (cumulative)" stroke="#93c5fd" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="actual" name="Actual (cumulative)" stroke="#065f46" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Monthly comparison table */}
          {projectedVsActual.monthly.length > 0 && (
            <>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', color: '#374151' }}>
                Monthly Comparison
              </h4>
              <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Projected Lbs</th>
                      <th>Actual Lbs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectedVsActual.monthly.map((row, idx) => (
                      <tr key={idx} style={{ cursor: 'default' }}>
                        <td style={{ fontWeight: 500 }}>{row.month}</td>
                        <td style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                          {parseFloat(row.projected_lbs || '0').toLocaleString()}
                        </td>
                        <td style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                          {parseFloat(row.actual_lbs || '0').toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Per-enrollment table */}
          {projectedVsActual.enrollments.length > 0 && (
            <>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', color: '#374151' }}>
                Per-Customer Breakdown
              </h4>
              <div style={{ overflowX: 'auto' }}>
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Chick Count</th>
                      <th>Start Date</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectedVsActual.enrollments.map((enrollment, idx) => (
                      <React.Fragment key={idx}>
                        <tr
                          style={{ cursor: 'pointer' }}
                          onClick={() => setExpandedEnrollmentIdx(expandedEnrollmentIdx === idx ? null : idx)}
                        >
                          <td>
                            <strong>{enrollment.customer_name}</strong>
                          </td>
                          <td style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                            {enrollment.chick_count}
                          </td>
                          <td style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                            {formatDate(enrollment.chick_start_date)}
                          </td>
                          <td>
                            <button className="btn btn-outline btn-sm">
                              {expandedEnrollmentIdx === idx ? 'Collapse' : 'Expand'}
                            </button>
                          </td>
                        </tr>
                        {expandedEnrollmentIdx === idx && enrollment.monthly.length > 0 && (
                          <tr style={{ cursor: 'default' }}>
                            <td colSpan={4} style={{ padding: 0 }}>
                              <div style={{ background: '#f9fafb', padding: '0.75rem 1rem' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ padding: '0.5rem', textAlign: 'left', fontSize: '0.8rem', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>
                                        Month
                                      </th>
                                      <th style={{ padding: '0.5rem', textAlign: 'left', fontSize: '0.8rem', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>
                                        Projected Lbs
                                      </th>
                                      <th style={{ padding: '0.5rem', textAlign: 'left', fontSize: '0.8rem', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>
                                        Actual Lbs
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {enrollment.monthly.map((m, mIdx) => (
                                      <tr key={mIdx}>
                                        <td style={{ padding: '0.4rem 0.5rem', fontSize: '0.9rem', borderBottom: '1px solid #f3f4f6' }}>
                                          {m.month}
                                        </td>
                                        <td style={{ padding: '0.4rem 0.5rem', fontSize: '0.9rem', borderBottom: '1px solid #f3f4f6' }}>
                                          {parseFloat(m.projected_lbs || '0').toLocaleString()}
                                        </td>
                                        <td style={{ padding: '0.4rem 0.5rem', fontSize: '0.9rem', borderBottom: '1px solid #f3f4f6' }}>
                                          {parseFloat(m.actual_lbs || '0').toLocaleString()}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ==================== Enroll Customer Modal ==================== */}
      {showEnrollModal && (
        <div className="modal-overlay" onClick={() => setShowEnrollModal(false)}>
          <div
            className="modal"
            style={{ maxWidth: '550px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Enroll Customer</h2>
              <button className="modal-close" onClick={() => setShowEnrollModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {enrollError && <div className="error-message">{enrollError}</div>}

              {/* Customer Search */}
              <div className="form-group" style={{ marginBottom: '1rem', position: 'relative' }}>
                <label>
                  Customer <span className="required">*</span>
                </label>
                {selectedCustomer ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.625rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: '#f9fafb',
                  }}>
                    <span style={{ fontWeight: 500 }}>
                      {selectedCustomer.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setCustomerSearch('');
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#6b7280',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        lineHeight: 1,
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      placeholder="Search customers..."
                      autoFocus
                    />
                    {searchLoading && (
                      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        Searching...
                      </div>
                    )}
                    {customerResults.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: '#fff',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          zIndex: 10,
                          maxHeight: '200px',
                          overflowY: 'auto',
                        }}
                      >
                        {customerResults.map((c) => (
                          <div
                            key={c.id}
                            onClick={() => {
                              setSelectedCustomer(c);
                              setCustomerSearch('');
                              setCustomerResults([]);
                            }}
                            style={{
                              padding: '0.625rem 0.75rem',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f3f4f6',
                              fontSize: '0.9rem',
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLElement).style.background = '#f3f4f6';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.background = '#fff';
                            }}
                          >
                            {c.name}
                          </div>
                        ))}
                      </div>
                    )}
                    {customerSearch.trim().length >= 2 && !searchLoading && customerResults.length === 0 && (
                      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        No customers found.
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Dynamic custom fields */}
              {customFieldDefs.length > 0 && (
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                  {customFieldDefs.map((def) => (
                    <div className="form-group" key={def.key} style={{ marginBottom: '1rem' }}>
                      <label>
                        {def.label}
                        {def.required && <span className="required"> *</span>}
                      </label>
                      {renderCustomFieldInput(def)}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEnrollModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleEnrollSubmit}
                disabled={enrolling || !selectedCustomer}
              >
                {enrolling ? 'Enrolling...' : 'Enroll'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCampaignDetail;
